import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, FileText, LogOut, ExternalLink, Loader2, ShieldCheck, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { adminAxios, useAuth } from '../lib/AuthContext';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const STATUS_STYLES = {
  draft: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
  finalized: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [polling, setPolling] = useState(false);
  const [pollResult, setPollResult] = useState(null);

  const handlePollNow = async () => {
    setPolling(true);
    setPollResult(null);
    try {
      const { data } = await adminAxios.post('/agent-poll-now');
      setPollResult(data);
    } catch (err) {
      setPollResult({ error: err.response?.data?.detail || 'Poll failed' });
    } finally {
      setPolling(false);
      setTimeout(() => setPollResult(null), 10000);
    }
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await adminAxios.get('/service-sheets');
        if (!cancel) setSheets(data);
      } catch (err) {
        if (!cancel) setError(err.response?.data?.detail || 'Failed to load sheets');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0d1829] text-white">
      <header className="border-b border-white/5 bg-[#0d1829]/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400">Deep Control · Engineer Portal</div>
            <div className="text-lg font-semibold mt-0.5" data-testid="admin-dashboard-title">Service Sign-Off Sheets</div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePollNow}
              disabled={polling}
              className="text-amber-300 hover:text-amber-200 hover:bg-amber-500/10"
              data-testid="admin-poll-now-button"
              title="Check ElevenLabs for any unprocessed customer calls right now"
            >
              {polling ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
              Poll leads
            </Button>
            <Link to="/admin/boss-users" className="hidden sm:inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white border border-white/10 hover:border-white/20 rounded-full px-3 py-1.5" data-testid="admin-boss-users-link">
              <ShieldCheck className="w-3.5 h-3.5" /> Boss Mode
            </Link>
            <span className="hidden sm:block text-sm text-slate-300" data-testid="admin-current-user">{user?.email}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-300 hover:text-white"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {pollResult && (
          <div
            className={`mb-4 text-sm rounded-md px-3 py-2 border ${
              pollResult.error
                ? 'bg-red-500/5 border-red-500/20 text-red-300'
                : 'bg-emerald-500/5 border-emerald-500/20 text-emerald-300'
            }`}
            data-testid="admin-poll-result"
          >
            {pollResult.error
              ? `Poll failed: ${pollResult.error}`
              : `Poll complete · ${pollResult.checked ?? 0} call(s) checked · ${pollResult.new ?? 0} new emailed${
                  pollResult.stale_skipped ? ` · ${pollResult.stale_skipped} stale skipped` : ''
                }`}
          </div>
        )}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold">Recent sheets</h2>
            <p className="text-sm text-slate-400 mt-1">All maintenance sign-offs logged through this device.</p>
          </div>
          <Link to="/admin/service-sheet/new">
            <Button className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="admin-new-sheet-button">
              <Plus className="w-4 h-4 mr-1.5" /> New sheet
            </Button>
          </Link>
        </div>

        {error && (
          <div className="text-sm text-red-400 bg-red-500/5 border border-red-500/20 rounded-md px-3 py-2 mb-4" data-testid="admin-dashboard-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-12 justify-center" data-testid="admin-dashboard-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading sheets…
          </div>
        ) : sheets.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl" data-testid="admin-dashboard-empty">
            <FileText className="w-10 h-10 text-slate-500 mx-auto mb-3" />
            <p className="text-slate-300 font-medium">No service sheets yet</p>
            <p className="text-sm text-slate-500 mt-1">Tap "New sheet" to log your first maintenance visit.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="admin-sheets-list">
            {sheets.map((s) => (
              <Link
                key={s.id}
                to={`/admin/service-sheet/${s.id}`}
                className="block bg-[#1e2d4a]/40 hover:bg-[#1e2d4a]/70 border border-white/10 rounded-xl px-4 py-3 transition"
                data-testid={`admin-sheet-row-${s.id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm text-blue-300">{s.sheetNumber}</span>
                      <span
                        className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_STYLES[s.status] || ''}`}
                      >
                        {s.status}
                      </span>
                    </div>
                    <div className="text-sm text-slate-200 mt-1 truncate">
                      {s.client?.name || 'Unknown client'} · {s.equipment?.type || 'Equipment'}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{formatDate(s.serviceDate || s.createdAt)}</div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-slate-500 flex-shrink-0" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
